import { Anchor, Avatar, Badge, Card, Group, Paper, SimpleGrid, Skeleton, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import {
  IconAlertTriangle, IconArrowRight, IconBriefcase, IconCircleCheck, IconDatabaseCog, IconInfoCircle, IconMail, IconSatellite,
  IconStack2, IconUsers,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useNavigate } from "react-router";

import { http } from "../../api/http";
import { useCan } from "../../auth/user";
import { Empty, fmtDateTime, PageHeader, plural } from "../../components/ui";
import { useT } from "../../i18n/script";
import AdminLayout, { useAdminSections } from "./AdminLayout";

interface CaseStats { ceka_dodjelu: number; otvoreni: number; preko_roka: number; na_dopuni?: number }
interface Sentinel { configured: boolean; enabled: boolean; month_requests: number; month_limit: number; month_pu?: number; pu_limit?: number }
interface Backup { name: string; size_bytes: number; created_at: string }
interface AuditItem { id: number; ts: string; username: string | null; modul: string; opis: string; ishod: string; uspjeh: boolean }
interface SettingsSection { code: string; items: { key: string; value: unknown }[] }

export const initials = (name?: string | null) => {
  if (!name) return "?";
  const p = name.split(/[.\s_-]+/).filter(Boolean);
  return ((p[0]?.[0] ?? "") + (p[1]?.[0] ?? "")).toUpperCase() || name.slice(0, 2).toUpperCase();
};

export const relTime = (ts: string) => {
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (s < 60) return "upravo";
  if (s < 3600) return `prije ${Math.floor(s / 60)} min`;
  if (s < 86400) return `prije ${Math.floor(s / 3600)} h`;
  if (s < 86400 * 7) return `prije ${Math.floor(s / 86400)} d`;
  return fmtDateTime(ts);
};

function Tile({ label, value, hint, icon, color, onClick, loading }: {
  label: string; value: ReactNode; hint?: ReactNode; icon: ReactNode; color?: string; onClick?: () => void; loading?: boolean }) {
  const { t } = useT();
  return (
    <Card withBorder radius="lg" padding="md" className={onClick ? "holding-card" : undefined} onClick={onClick}>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={2} miw={0}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{t(label)}</Text>
          {loading ? <Skeleton h={28} w={60} mt={2} /> : <Text fz={typeof value === "number" || /^[\d/ ]+$/.test(String(value)) ? 26 : 18} fw={750} lh={1.25}>{value}</Text>}
          {hint && <Text size="xs" c="dimmed">{hint}</Text>}
        </Stack>
        <ThemeIcon size={40} radius="md" variant="light" color={color ?? "agri"}>{icon}</ThemeIcon>
      </Group>
    </Card>
  );
}

interface Attention { key: string; color: string; icon: ReactNode; title: string; text: string; to?: string }

export default function AdminHub() {
  const { t } = useT();
  const can = useCan();
  const nav = useNavigate();
  const sections = useAdminSections();

  const cases = useQuery({ queryKey: ["cases-stats"], enabled: can("cases:read"), queryFn: () => http.get<CaseStats>("/api/v1/cases/stats") });
  const pending = useQuery({ queryKey: ["layers-manage", "predlozen"], enabled: can("layers:propose"),
    queryFn: () => http.get<{ id: number; title: string; proposed_by: string | null }[]>("/api/v1/layers/manage", { status: "predlozen" }) });
  const users = useQuery({ queryKey: ["admin-users-count"], enabled: can("admin:users"),
    queryFn: () => http.get<{ total: number }>("/api/v1/admin/users", { size: 1, page: 1 }) });
  const sat = useQuery({ queryKey: ["sentinel-status"], queryFn: () => http.get<Sentinel>("/api/v1/sentinel/status"), retry: false });
  const backups = useQuery({ queryKey: ["backups"], enabled: can("system:backup"), queryFn: () => http.get<Backup[]>("/api/v1/system/backups") });
  const recent = useQuery({ queryKey: ["audit-recent"], enabled: can("audit:read"),
    queryFn: () => http.get<{ items: AuditItem[] }>("/api/v1/audit", { changes_only: true, limit: 8 }) });
  const settings = useQuery({ queryKey: ["settings"], enabled: can("system:params"), queryFn: () => http.get<SettingsSection[]>("/api/v1/settings") });

  const lastBackup = [...(backups.data ?? [])].sort((a, b) => b.created_at.localeCompare(a.created_at))[0];
  const backupAgeH = lastBackup ? (Date.now() - new Date(lastBackup.created_at).getTime()) / 3.6e6 : null;
  const smtpHost = settings.data?.find((s) => s.code === "smtp")?.items.find((i) => i.key === "smtp.host")?.value;

  const attention: Attention[] = [];
  if (pending.data?.length) attention.push({ key: "layers", color: "orange", icon: <IconStack2 size={18} />,
    title: `${pending.data.length} ${t(plural(pending.data.length, "sloj čeka odobrenje", "sloja čekaju odobrenje", "slojeva čeka odobrenje"))}`,
    text: pending.data.slice(0, 3).map((l) => `„${l.title}“`).join(", ") + (pending.data.length > 3 ? " …" : ""), to: "/admin/slojevi" });
  if (cases.data?.ceka_dodjelu) attention.push({ key: "cases", color: "blue", icon: <IconBriefcase size={18} />,
    title: `${cases.data.ceka_dodjelu} ${t(plural(cases.data.ceka_dodjelu, "predmet čeka dodjelu referentu", "predmeta čekaju dodjelu referentu", "predmeta čeka dodjelu referentu"))}`, text: t("Dodijelite ih kako bi obrada počela u roku."), to: "/predmeti" });
  if (cases.data?.preko_roka) attention.push({ key: "late", color: "red", icon: <IconAlertTriangle size={18} />,
    title: `${cases.data.preko_roka} ${t(plural(cases.data.preko_roka, "predmet je preko roka", "predmeta su preko roka", "predmeta je preko roka"))}`, text: t("Rok za obradu je istekao."), to: "/predmeti" });
  if (sat.data && !sat.data.configured) attention.push({ key: "sat", color: "gray", icon: <IconSatellite size={18} />,
    title: t("Satelitske analize nisu podešene"), text: t("Unesite besplatan Copernicus OAuth klijent u podešavanjima."),
    to: can("system:params") ? "/admin/podesavanja?sekcija=satelit" : undefined });
  const satReq = sat.data?.month_limit ? sat.data.month_requests / sat.data.month_limit : 0;
  const satPu = sat.data?.pu_limit ? (sat.data.month_pu ?? 0) / sat.data.pu_limit : 0;
  if (sat.data?.configured && Math.max(satReq, satPu) > 0.8) attention.push({ key: "satq",
    color: "orange", icon: <IconSatellite size={18} />, title: t("Mjesečna satelitska kvota je skoro potrošena"),
    text: `${sat.data.month_requests} / ${sat.data.month_limit} ${t("upita")} · ${Math.round(sat.data.month_pu ?? 0)} / ${sat.data.pu_limit} PU`,
    to: "/admin/podesavanja?sekcija=satelit" });
  if (typeof smtpHost === "string" && /mailpit|localhost|127\.0\.0\.1/.test(smtpHost)) attention.push({ key: "smtp", color: "yellow",
    icon: <IconMail size={18} />, title: t("E-pošta ide na razvojni server (Mailpit)"),
    text: t("Poruke ne stižu stvarnim korisnicima — prije produkcije unesite pravi SMTP server."), to: "/admin/podesavanja?sekcija=smtp" });
  if (backupAgeH !== null && backupAgeH > 36) attention.push({ key: "bk", color: "red", icon: <IconDatabaseCog size={18} />,
    title: t("Posljednji backup je stariji od 36 sati"), text: fmtDateTime(lastBackup?.created_at), to: "/admin/backup" });
  else if (can("system:backup") && backups.data && !backups.data.length) attention.push({ key: "bk0", color: "red", icon: <IconDatabaseCog size={18} />,
    title: t("Nema nijednog backup-a baze"), text: t("Pokrenite ručni backup."), to: "/admin/backup" });

  const quick = sections.filter((s) => s.title).flatMap((s) => s.items.map((i) => ({ ...i, section: s.title })));

  return (
    <AdminLayout>
      <PageHeader title="Administracija sistema" description="Stanje sistema na jednom mjestu: šta čeka vašu odluku, posljednje izmjene i brz pristup svim podešavanjima." />
      <SimpleGrid cols={{ base: 1, xs: 2, lg: 3, xl: 5 }} spacing="sm" mb="md">
        {can("cases:read") && (
          <Tile label="Predmeti na čekanju" icon={<IconBriefcase size={20} />} color="blue" loading={cases.isLoading}
            value={cases.data?.ceka_dodjelu ?? "—"} onClick={() => nav("/predmeti")}
            hint={cases.data ? <>{t("otvoreno")} {cases.data.otvoreni} · <Text span c={cases.data.preko_roka ? "red" : "dimmed"} size="xs" fw={cases.data.preko_roka ? 700 : 400}>{t("preko roka")} {cases.data.preko_roka}</Text></> : undefined} />
        )}
        {can("layers:propose") && (
          <Tile label="Slojevi na odobrenju" icon={<IconStack2 size={20} />} color={pending.data?.length ? "orange" : "agri"}
            loading={pending.isLoading} value={pending.data?.length ?? "—"} onClick={() => nav("/admin/slojevi")}
            hint={pending.data?.length ? t("čekaju odluku administratora") : t("sve je odobreno")} />
        )}
        {can("admin:users") && (
          <Tile label="Korisnici" icon={<IconUsers size={20} />} loading={users.isLoading} value={users.data?.total ?? "—"}
            onClick={() => nav("/admin/korisnici")} hint={t("nalozi u Keycloak-u")} />
        )}
        <Tile label="Satelitski podaci" icon={<IconSatellite size={20} />} color={sat.data?.configured ? "agri" : "gray"}
          loading={sat.isLoading}
          value={sat.data ? (sat.data.configured ? `${sat.data.month_requests}/${sat.data.month_limit}` : t("nije podešeno")) : "—"}
          hint={sat.data?.configured ? (sat.data.enabled ? `${t("upita ovog mjeseca")} · ${Math.round(sat.data.month_pu ?? 0)}/${sat.data.pu_limit ?? "—"} PU`
            : t("podešeno, ali isključeno")) : t("Copernicus Data Space")}
          onClick={can("system:params") ? () => nav("/admin/podesavanja?sekcija=satelit") : undefined} />
        {can("system:backup") && (
          <Tile label="Posljednji backup" icon={<IconDatabaseCog size={20} />} color={backupAgeH !== null && backupAgeH < 36 ? "agri" : "red"}
            loading={backups.isLoading} value={lastBackup ? relTime(lastBackup.created_at) : t("nema")}
            hint={lastBackup ? fmtDateTime(lastBackup.created_at) : undefined} onClick={() => nav("/admin/backup")} />
        )}
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md" mb="lg">
        <Paper withBorder radius="lg" p="md">
          <Group justify="space-between" mb="sm">
            <Title order={5}>{t("Zahtijeva pažnju")}</Title>
            {attention.length > 0 && <Badge color="orange" variant="light">{attention.length}</Badge>}
          </Group>
          {attention.length === 0 ? (
            <Group gap="xs" py="md" justify="center"><IconCircleCheck color="var(--mantine-color-agri-6)" />
              <Text c="dimmed" size="sm">{t("Sve je u redu — ništa ne čeka vašu odluku.")}</Text></Group>
          ) : (
            <Stack gap={6}>
              {attention.map((a) => (
                <Group key={a.key} wrap="nowrap" gap="sm" p={8} align="flex-start"
                  style={{ borderRadius: 10, cursor: a.to ? "pointer" : undefined, background: "var(--mantine-color-default-hover)" }}
                  onClick={() => a.to && nav(a.to)}>
                  <ThemeIcon variant="light" color={a.color} radius="md">{a.icon}</ThemeIcon>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text size="sm" fw={650}>{a.title}</Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>{a.text}</Text>
                  </div>
                  {a.to && <IconArrowRight size={16} opacity={0.5} style={{ marginTop: 4 }} />}
                </Group>
              ))}
            </Stack>
          )}
        </Paper>

        {can("audit:read") && (
          <Paper withBorder radius="lg" p="md">
            <Group justify="space-between" mb="sm">
              <Title order={5}>{t("Posljednje izmjene")}</Title>
              <Anchor size="sm" onClick={() => nav("/admin/audit")}>{t("Sva istorija")} →</Anchor>
            </Group>
            {recent.isLoading ? <Stack gap={8}>{[0, 1, 2, 3].map((i) => <Skeleton key={i} h={34} />)}</Stack>
              : !recent.data?.items.length ? <Empty text="Još nema zabilježenih izmjena" /> : (
                <Stack gap={10}>
                  {recent.data.items.map((r) => (
                    <Group key={r.id} wrap="nowrap" gap="sm" align="flex-start" style={{ cursor: "pointer" }}
                      onClick={() => nav(`/admin/audit?zapis=${r.id}`)}>
                      <Avatar size={30} radius="xl" color="agri" variant="light">{initials(r.username)}</Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text size="sm" lineClamp={2}><b>{r.username ?? t("sistem")}</b> — {r.opis}</Text>
                        <Group gap={6}>
                          <Badge size="xs" variant="light" color="gray">{r.modul}</Badge>
                          {!r.uspjeh && <Badge size="xs" color="red" variant="light">{r.ishod}</Badge>}
                          <Text size="xs" c="dimmed">{relTime(r.ts)}</Text>
                        </Group>
                      </div>
                    </Group>
                  ))}
                </Stack>
              )}
          </Paper>
        )}
      </SimpleGrid>

      <Title order={5} mb="sm">{t("Brz pristup")}</Title>
      <SimpleGrid cols={{ base: 1, sm: 2, xl: 3 }} spacing="sm">
        {quick.map((c) => (
          <Card key={c.to} withBorder radius="lg" padding="md" className="holding-card"
            onClick={() => (c.external ? window.open(c.to, "_blank", "noopener") : nav(c.to))}>
            <Group wrap="nowrap" align="flex-start">
              <ThemeIcon size={40} radius="md" variant="light" color="agri">{c.icon}</ThemeIcon>
              <div style={{ minWidth: 0 }}>
                <Group gap={6}>
                  <Text fw={650}>{t(c.label)}</Text>
                  {c.to === "/admin/slojevi" && !!pending.data?.length && <Badge size="xs" color="orange">{pending.data.length}</Badge>}
                </Group>
                <Text size="xs" c="dimmed">{t(c.desc ?? "")}</Text>
                <Text size="xs" c="dimmed" mt={4} tt="uppercase" fw={600} style={{ letterSpacing: ".03em", opacity: 0.7 }}>{t(c.section)}</Text>
              </div>
            </Group>
          </Card>
        ))}
      </SimpleGrid>
      <Group gap={6} mt="md"><IconInfoCircle size={14} opacity={0.6} />
        <Text size="xs" c="dimmed">{t("Prikazani su samo odjeljci za koje imate ovlašćenje.")}</Text></Group>
    </AdminLayout>
  );
}
